import { useEffect, useMemo, useState, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, ArrowRight, Loader2, Search, Users, X } from "lucide-react";
import { cn } from "@/lib/utils";

interface Broker {
  user_id: string;
  display_name: string;
  secondary_name?: string;
  /** All groups this broker is in (across the shipper's networks). */
  network_ids: string[];
}

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  targetNetworkId: string;
  targetNetworkName: string;
  allNetworks: { id: string; name: string }[];
  shipperUserId: string;
  shipperCompanyId?: string | null;
  onMutated: () => void;
}

function initialsOf(s: string): string {
  const parts = s.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

/**
 * Two-panel transfer UI to organize existing network brokers into a group.
 * - LEFT: Your Brokers (in shipper's network, NOT in this group).
 * - RIGHT: In This Group.
 * - Move left→right to add, right→left to remove.
 * - Removals use the safe group-scoped path (status='removed'), blocked if
 *   it would leave the broker in zero groups.
 * - Only ONE save button at the bottom.
 */
export default function ManageGroupBrokersSheet({
  open, onOpenChange, targetNetworkId, targetNetworkName,
  shipperUserId, shipperCompanyId, onMutated,
}: Props) {
  const [brokers, setBrokers] = useState<Broker[]>([]);
  const [initialInTarget, setInitialInTarget] = useState<Set<string>>(new Set());
  const [inTarget, setInTarget] = useState<Set<string>>(new Set());
  /** Row ids in target group (for the safe removal update). */
  const [targetRowByUser, setTargetRowByUser] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [leftQuery, setLeftQuery] = useState("");
  const [rightQuery, setRightQuery] = useState("");

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [{ data: byUser }, { data: byCompany }] = await Promise.all([
        supabase.from("shipper_private_networks" as any).select("id").eq("shipper_user_id", shipperUserId),
        shipperCompanyId
          ? supabase.from("shipper_private_networks" as any).select("id").eq("shipper_company_id", shipperCompanyId)
          : Promise.resolve({ data: [] as any[] }),
      ]);
      const netIds = Array.from(new Set([
        ...((byUser as any[]) || []).map((r) => r.id),
        ...((byCompany as any[]) || []).map((r) => r.id),
      ]));
      if (!netIds.length) {
        if (!cancelled) {
          setBrokers([]); setInitialInTarget(new Set()); setInTarget(new Set());
          setTargetRowByUser(new Map()); setLoading(false);
        }
        return;
      }

      const { data: memberships } = await supabase
        .from("shipper_network_brokers" as any)
        .select("id, network_id, broker_user_id, broker_company_id")
        .in("network_id", netIds)
        .eq("status", "accepted")
        .is("removed_at", null);

      const rows = ((memberships as any[]) || []).filter((r) => r.broker_user_id);

      const byUserMap = new Map<string, { user_id: string; company_id: string | null; network_ids: string[] }>();
      const inTargetIds = new Set<string>();
      const rowMap = new Map<string, string>();
      for (const r of rows) {
        if (r.network_id === targetNetworkId) {
          inTargetIds.add(r.broker_user_id);
          rowMap.set(r.broker_user_id, r.id);
        }
        const ex = byUserMap.get(r.broker_user_id);
        if (ex) {
          if (!ex.network_ids.includes(r.network_id)) ex.network_ids.push(r.network_id);
        } else {
          byUserMap.set(r.broker_user_id, {
            user_id: r.broker_user_id,
            company_id: r.broker_company_id ?? null,
            network_ids: [r.network_id],
          });
        }
      }

      const userIds = Array.from(byUserMap.keys());
      const companyIds = Array.from(new Set(
        Array.from(byUserMap.values()).map((b) => b.company_id).filter((x): x is string => !!x)
      ));

      const [{ data: profs }, { data: cos }] = await Promise.all([
        userIds.length
          ? (supabase as any).from("profiles").select("id, first_name, last_name, email").in("id", userIds)
          : Promise.resolve({ data: [] as any[] }),
        companyIds.length
          ? supabase.from("companies").select("id, name").in("id", companyIds)
          : Promise.resolve({ data: [] as any[] }),
      ]);
      const profMap = new Map((profs as any[] || []).map((p) => [p.id, p]));
      const coMap = new Map<string, string>(
        ((cos as any[]) || []).map((c) => [c.id as string, (c.name as string) || ""])
      );

      const list: Broker[] = Array.from(byUserMap.values()).map((b) => {
        const p = profMap.get(b.user_id) as any;
        const personName = p ? [p.first_name, p.last_name].filter(Boolean).join(" ").trim() : "";
        const coName = b.company_id ? coMap.get(b.company_id) : undefined;
        return {
          user_id: b.user_id,
          display_name: String(coName || personName || p?.email || "Broker"),
          secondary_name: coName && personName ? personName : (coName && p?.email) || undefined,
          network_ids: b.network_ids,
        };
      });
      list.sort((a, b) => String(a.display_name).localeCompare(String(b.display_name)));

      if (!cancelled) {
        setBrokers(list);
        setInitialInTarget(new Set(inTargetIds));
        setInTarget(new Set(inTargetIds));
        setTargetRowByUser(rowMap);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, targetNetworkId, shipperUserId, shipperCompanyId]);

  const byId = useMemo(() => new Map(brokers.map((b) => [b.user_id, b])), [brokers]);

  const leftList = useMemo(() => {
    const q = leftQuery.trim().toLowerCase();
    return brokers
      .filter((b) => !inTarget.has(b.user_id))
      .filter((b) => !q || `${b.display_name} ${b.secondary_name ?? ""}`.toLowerCase().includes(q));
  }, [brokers, inTarget, leftQuery]);

  const rightList = useMemo(() => {
    const q = rightQuery.trim().toLowerCase();
    return brokers
      .filter((b) => inTarget.has(b.user_id))
      .filter((b) => !q || `${b.display_name} ${b.secondary_name ?? ""}`.toLowerCase().includes(q));
  }, [brokers, inTarget, rightQuery]);

  const moveRight = (id: string) => setInTarget((p) => { const n = new Set(p); n.add(id); return n; });
  const moveLeft = (id: string) => {
    const b = byId.get(id);
    if (!b) return;
    // Zero-groups guardrail (pre-check UX): if this broker is only in the target group,
    // moving them out via this popup would leave them in 0 groups. Block with a hint —
    // the actual DB-safe check runs again at save.
    const wasInitially = initialInTarget.has(id);
    if (wasInitially) {
      const otherGroups = b.network_ids.filter((nid) => nid !== targetNetworkId).length;
      if (otherGroups < 1) {
        toast.warning(
          `${b.display_name} is only in this group — use "Remove from network" to remove them entirely.`,
          { duration: 5000 }
        );
        return;
      }
    }
    setInTarget((p) => { const n = new Set(p); n.delete(id); return n; });
  };

  const moveAllRight = () => setInTarget(new Set(brokers.map((b) => b.user_id)));
  const moveAllLeft = () => {
    // Only move brokers that are safe to remove (still in another group after removal).
    const next = new Set<string>();
    let blocked = 0;
    for (const b of brokers) {
      if (!inTarget.has(b.user_id)) continue;
      const wasInitially = initialInTarget.has(b.user_id);
      const otherGroups = b.network_ids.filter((nid) => nid !== targetNetworkId).length;
      if (wasInitially && otherGroups < 1) {
        next.add(b.user_id); // keep in target
        blocked++;
      }
    }
    setInTarget(next);
    if (blocked > 0) {
      toast.warning(`${blocked} broker(s) kept — they'd have no other group. Use "Remove from network" to remove entirely.`);
    }
  };

  const handleSave = async () => {
    // Diff against initial
    const toAdd: Broker[] = [];
    const toRemove: Broker[] = [];
    for (const b of brokers) {
      const was = initialInTarget.has(b.user_id);
      const now = inTarget.has(b.user_id);
      if (!was && now) toAdd.push(b);
      else if (was && !now) toRemove.push(b);
    }

    // Enforce guardrail again at save
    const blocked: Broker[] = [];
    const safeRemoves: Broker[] = [];
    for (const b of toRemove) {
      const otherGroups = b.network_ids.filter((nid) => nid !== targetNetworkId).length;
      if (otherGroups < 1) blocked.push(b);
      else safeRemoves.push(b);
    }

    if (!toAdd.length && !safeRemoves.length && !blocked.length) {
      onOpenChange(false);
      return;
    }

    setSubmitting(true);
    try {
      if (toAdd.length) {
        const { error } = await supabase.rpc("add_brokers_to_network" as any, {
          p_network_id: targetNetworkId,
          p_broker_user_ids: toAdd.map((b) => b.user_id),
        });
        if (error) throw error;
      }
      if (safeRemoves.length) {
        const rowIds = safeRemoves
          .map((b) => targetRowByUser.get(b.user_id))
          .filter((x): x is string => !!x);
        if (rowIds.length) {
          const { error } = await supabase
            .from("shipper_network_brokers" as any)
            .update({ status: "removed", removed_at: new Date().toISOString() })
            .in("id", rowIds);
          if (error) throw error;
        }
      }

      const parts: string[] = [];
      if (toAdd.length) parts.push(`${toAdd.length} added`);
      if (safeRemoves.length) parts.push(`${safeRemoves.length} removed`);
      if (parts.length) toast.success(`${targetNetworkName}: ${parts.join(", ")}`);

      if (blocked.length) {
        const names = blocked.slice(0, 3).map((b) => b.display_name).join(", ");
        const extra = blocked.length > 3 ? ` and ${blocked.length - 3} more` : "";
        toast.warning(
          `Kept in group: ${names}${extra} — this is their only group. Use "Remove from network" to remove entirely.`,
          { duration: 6000 }
        );
      }
      onMutated();
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e?.message || "Failed to save changes");
    } finally {
      setSubmitting(false);
    }
  };

  const pendingChanges =
    Array.from(inTarget).filter((id) => !initialInTarget.has(id)).length +
    Array.from(initialInTarget).filter((id) => !inTarget.has(id)).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "p-0 gap-0 max-w-none w-screen h-[100dvh] sm:h-[85vh] sm:w-[92vw] sm:max-w-4xl sm:rounded-lg",
          "flex flex-col"
        )}
      >
        {/* Header (no save button here — save is only at the bottom) */}
        <div className="px-4 sm:px-5 py-3 border-b border-border flex items-center gap-2 shrink-0">
          <Users className="h-4 w-4 text-brand-teal shrink-0" />
          <h2 className="text-sm font-bold uppercase tracking-wider truncate">
            Manage Brokers — {targetNetworkName}
          </h2>
          <div className="ml-auto text-xs text-muted-foreground hidden sm:block">
            Move brokers into or out of this group.
          </div>
        </div>

        {/* Two panels */}
        <div className="flex-1 min-h-0 grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-0">
          {/* LEFT: Your Brokers */}
          <TransferPanel
            title="Your Brokers"
            subtitle="Not in this group"
            count={leftList.length}
            query={leftQuery}
            onQuery={setLeftQuery}
            items={leftList}
            actionIcon={<ArrowRight className="h-3.5 w-3.5" />}
            actionLabel="Add to group"
            onItemAction={moveRight}
            emptyMessage={brokers.length === 0 ? "No brokers in your network yet — invite some first." : "All your brokers are in this group."}
            loading={loading}
          />

          {/* Center controls */}
          <div className="hidden sm:flex flex-col items-center justify-center gap-2 px-3 border-x border-border bg-muted/20">
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={moveAllRight}
              disabled={loading || submitting || leftList.length === 0}
              title="Add all to group"
            >
              <ArrowRight className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={moveAllLeft}
              disabled={loading || submitting || rightList.length === 0}
              title="Remove all from group"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </div>

          {/* RIGHT: In This Group */}
          <TransferPanel
            title="In This Group"
            subtitle={targetNetworkName}
            count={rightList.length}
            query={rightQuery}
            onQuery={setRightQuery}
            items={rightList}
            actionIcon={<ArrowLeft className="h-3.5 w-3.5" />}
            actionLabel="Remove from group"
            onItemAction={moveLeft}
            emptyMessage="No brokers in this group yet. Click a broker on the left to add them."
            loading={loading}
            accent
          />
        </div>

        {/* Footer — the ONLY save button */}
        <div className="px-4 sm:px-5 py-3 border-t border-border flex items-center justify-between gap-2 shrink-0">
          <div className="text-xs text-muted-foreground">
            {pendingChanges > 0
              ? <><span className="font-semibold text-foreground">{pendingChanges}</span> pending change{pendingChanges === 1 ? "" : "s"}</>
              : "No changes yet"}
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" variant="ghost" size="sm" className="h-9" onClick={() => onOpenChange(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              className="h-9 text-white gap-1.5"
              style={{ backgroundColor: "hsl(var(--brand-teal))" }}
              onClick={handleSave}
              disabled={submitting || loading || pendingChanges === 0}
            >
              {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {submitting ? "Saving…" : `Save${pendingChanges > 0 ? ` (${pendingChanges})` : ""}`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface TransferPanelProps {
  title: string;
  subtitle: string;
  count: number;
  query: string;
  onQuery: (v: string) => void;
  items: Broker[];
  actionIcon: ReactNode;
  actionLabel: string;
  onItemAction: (id: string) => void;
  emptyMessage: string;
  loading: boolean;
  accent?: boolean;
}

function TransferPanel({
  title, subtitle, count, query, onQuery, items, actionIcon, actionLabel, onItemAction, emptyMessage, loading, accent,
}: TransferPanelProps) {
  return (
    <div className="flex flex-col min-h-0">
      <div className={cn("px-3 py-2 border-b border-border shrink-0", accent ? "bg-brand-teal/5" : "bg-card/40")}>
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[11px] font-bold uppercase tracking-wider text-foreground">{title}</div>
            <div className="text-[11px] text-muted-foreground truncate">{subtitle}</div>
          </div>
          <span className="text-xs font-semibold text-foreground shrink-0">{count}</span>
        </div>
        <div className="mt-2 flex items-center gap-2 rounded-md border border-border bg-background px-2">
          <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <Input
            value={query}
            onChange={(e) => onQuery(e.target.value)}
            placeholder="Search…"
            className="h-8 border-0 shadow-none focus-visible:ring-0 px-0 text-sm"
          />
          {query && (
            <button type="button" onClick={() => onQuery("")} aria-label="Clear search" className="p-1 hover:bg-muted rounded-sm">
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto p-2 space-y-1.5">
        {loading ? (
          <div className="text-sm text-muted-foreground text-center py-12">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-xs text-muted-foreground text-center py-12 px-4">{emptyMessage}</div>
        ) : (
          items.map((b) => (
            <button
              key={b.user_id}
              type="button"
              onClick={() => onItemAction(b.user_id)}
              title={actionLabel}
              className={cn(
                "w-full text-left rounded-md border border-border bg-card hover:bg-muted/60 hover:border-brand-teal/40",
                "px-2.5 py-2 flex items-center gap-2.5 transition-colors group"
              )}
            >
              <div className="h-9 w-9 rounded-full bg-brand-teal/15 text-brand-teal flex items-center justify-center text-xs font-bold shrink-0">
                {initialsOf(b.display_name)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-foreground truncate">{b.display_name}</div>
                {b.secondary_name && (
                  <div className="text-[11px] text-muted-foreground truncate">{b.secondary_name}</div>
                )}
              </div>
              <span className="h-6 w-6 rounded-full flex items-center justify-center text-muted-foreground group-hover:text-brand-teal group-hover:bg-brand-teal/10 shrink-0">
                {actionIcon}
              </span>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
